import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import client from "../../../../api/client";

const errMsg = (e, fallback) => e?.response?.data?.message || fallback;
const FIELDS = [["{first_name}", "first name"], ["{course}", "course"], ["{attendance}", "attendance %"], ["{activities}", "activities %"], ["{missed}", "classes missed in a row"]];

/** What the server fills in for one student, so the preview matches what they'll get. */
const fill = (text, s, course) => {
  const missed = [...(s.classes || [])].reverse().findIndex((c) => c !== "absent");
  const vars = {
    "{first_name}": (s.name || "").split(" ")[0] || "there",
    "{course}": course,
    "{attendance}": s.attendance?.percent == null ? "not counted yet" : `${s.attendance.percent}%`,
    "{activities}": s.activity == null ? "not counted yet" : `${s.activity}%`,
    "{missed}": String(missed === -1 ? (s.classes || []).length : missed),
  };
  return Object.entries(vars).reduce((t, [k, v]) => t.split(k).join(v), text);
};

/**
 * Email one or more students from the dashboard. It goes from E1X Academy in the branded template,
 * signed with your name, and is logged so other staff can see who was contacted.
 */
export default function EmailComposer({ scope, course, recipients: initial, onCancel, onSent }) {
  const [recipients, setRecipients] = useState(initial);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    client.get("/staff/email/templates").then((r) => {
      setTemplates(r.data);
      const first = r.data.find((t) => t.key === (initial.length === 1 && initial[0].status === "risk" ? "check_in" : "custom")) || r.data[0];
      if (first) { setTemplate(first.key); setSubject(first.subject); setBody(first.body); }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (key) => {
    const t = templates.find((x) => x.key === key);
    setTemplate(key);
    if (t) { setSubject(t.subject); setBody(t.body); }
  };
  const sample = recipients[0];
  const shown = useMemo(() => (sample ? { subject: fill(subject, sample, course), body: fill(body, sample, course) } : null), [sample, subject, body, course]);

  const send = async (e) => {
    e.preventDefault();
    if (!recipients.length || !subject.trim() || !body.trim()) return;
    setBusy(true);
    try {
      const r = await client.post("/staff/email", { cohort_id: scope.cohort_id, course_id: scope.course_id, user_ids: recipients.map((s) => s.id), subject: subject.trim(), body: body.trim(), template });
      toast.success(r.data.message);
      onSent?.();
    } catch (err) {
      toast.error(errMsg(err, "We couldn't send the email."));
      setBusy(false);
    }
  };

  return (
    <div className="sp-modal-wrap">
      <form className="sp-modal wide" role="dialog" aria-modal="true" aria-labelledby="em-t" onSubmit={send}>
        <h3 id="em-t">Email {recipients.length === 1 ? recipients[0].name : `${recipients.length} students`}</h3>
        <div className="sp-to">
          <span>To</span>
          {recipients.map((s) => (
            <span key={s.id} className="sp-chip">{s.name}{recipients.length > 1 && <button type="button" aria-label={`Remove ${s.name}`} onClick={() => setRecipients(recipients.filter((x) => x.id !== s.id))}>✕</button>}</span>
          ))}
        </div>
        <label className="sp-field" htmlFor="em-tpl">Template
          <select id="em-tpl" value={template} onChange={(e) => pick(e.target.value)}>{templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select>
        </label>
        {preview && shown ? (
          <div className="sp-preview">
            <small>Preview for {sample.name} · from E1X Academy</small>
            <strong>{shown.subject}</strong>
            <p>{shown.body}</p>
          </div>
        ) : (
          <>
            <label className="sp-field" htmlFor="em-sub">Subject<input id="em-sub" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150} required /></label>
            <label className="sp-field" htmlFor="em-body">Message<textarea id="em-body" rows={9} value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} required /></label>
            <p className="course-tag" style={{ margin: 0, fontSize: 12.5 }}>Filled in for each student: {FIELDS.map(([k, l], i) => <span key={k}>{i ? ", " : ""}<code>{k}</code> {l}</span>)}.</p>
          </>
        )}
        <div className="sp-warn">Sent from E1X Academy in the branded email template, signed with your name. Replies go to the E1X inbox.</div>
        <div className="sp-acts" style={{ justifyContent: "flex-end" }}>
          <button className="cbtn quiet" type="button" onClick={() => setPreview(!preview)} disabled={!sample}>{preview ? "Edit" : "Preview"}</button>
          <button className="cbtn quiet" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="cbtn go" type="submit" disabled={busy || !recipients.length}>{busy ? "Sending…" : `Send to ${recipients.length === 1 ? recipients[0].name.split(" ")[0] : `${recipients.length} students`}`}</button>
        </div>
      </form>
    </div>
  );
}
